import { useCallback, useMemo, useState } from "react";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  deleteEmployeeTime,
  formatHours,
  listEmployeeTimes,
  type EmployeeTimeEntry,
} from "@/lib/employeeTimes";
import {
  EmployeeLockedAutocomplete,
  type EmployeeOption,
} from "@/pages/admin/zeiterfassung/EmployeeLockedAutocomplete";
import {
  ENTRIES_OVERVIEW_COLUMNS,
  formatEntryDateLabel,
  formatEntryProjectLabel,
  formatEntryProjectSubtitle,
  formatEntryTimeRange,
  formatEntryTypeLabel,
  getEntriesColumnDefaultWidth,
  getEntriesColumnLabel,
} from "@/pages/admin/zeiterfassung/columns";
import { EmployeeTimeTimesheetBadge } from "@/pages/admin/zeiterfassung/EmployeeTimeTimesheetBadge";
import { useRefreshKeyLoad } from "@/pages/admin/zeiterfassung/useRefreshKeyLoad";
import { useZeiterfassungColumnWidths } from "@/pages/admin/zeiterfassung/useZeiterfassungColumnWidths";
import {
  ZeiterfassungDataTable,
  type ZeiterfassungTableColumn,
} from "@/pages/admin/zeiterfassung/ZeiterfassungDataTable";

interface EntriesOverviewProps {
  refreshKey: number;
  onChanged: () => void;
}

export function EntriesOverview({ refreshKey, onChanged }: EntriesOverviewProps) {
  const [entries, setEntries] = useState<EmployeeTimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [employeeFilter, setEmployeeFilter] = useState<AutocompleteOption<EmployeeOption> | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const { widths, onColumnWidthChange } = useZeiterfassungColumnWidths("entries-overview");

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const rows = await listEmployeeTimes({
        user_login: employeeFilter?.value.login || undefined,
        work_date_from: dateFrom || undefined,
        work_date_to: dateTo || undefined,
      });
      setEntries(rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Einträge konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [employeeFilter, dateFrom, dateTo]);

  useRefreshKeyLoad(load, refreshKey);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Diesen Zeiteintrag wirklich löschen?")) return;
    setDeletingId(id);
    try {
      await deleteEmployeeTime(id);
      onChanged();
      await load({ silent: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  const totalHours = entries.reduce(
    (sum, e) => (e.entry_type === "break" ? sum : sum + Number(e.hours_decimal || 0)),
    0,
  );

  const columns = useMemo(
    (): ZeiterfassungTableColumn<EmployeeTimeEntry>[] =>
      ENTRIES_OVERVIEW_COLUMNS.map((key) => ({
        key,
        label: getEntriesColumnLabel(key),
        defaultWidth: getEntriesColumnDefaultWidth(key),
        mono: key === "hours" || key === "time_range",
        align: key === "hours" ? "right" : "left",
        className: key === "hours" ? "font-medium" : undefined,
        sortValue: (entry) => {
          switch (key) {
            case "work_date":
              return entry.work_date;
            case "employee":
              return entry.user_display || entry.user_login;
            case "project":
              return entry.entry_type === "break"
                ? "Pause"
                : [formatEntryProjectLabel(entry), formatEntryProjectSubtitle(entry)]
                    .filter(Boolean)
                    .join(" ");
            case "hours":
              return Number(entry.hours_decimal || 0);
            case "time_range":
              return formatEntryTimeRange(entry);
            case "entry_type":
              return formatEntryTypeLabel(entry.entry_type);
            case "homeoffice":
              return entry.entry_type !== "break" && entry.is_homeoffice ? "Ja" : "";
            case "comment":
              return entry.comment;
            default:
              return "";
          }
        },
        render: (entry) => {
          switch (key) {
            case "work_date":
              return formatEntryDateLabel(entry.work_date);
            case "employee":
              return entry.user_display || entry.user_login;
            case "project":
              if (entry.entry_type === "break") {
                return (
                  <span className="inline-flex rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-semibold text-label-secondary">
                    Pause
                  </span>
                );
              }
              return formatEntryProjectLabel(entry) ? (
                <>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium">{formatEntryProjectLabel(entry)}</span>
                    <EmployeeTimeTimesheetBadge entry={entry} />
                  </div>
                  {formatEntryProjectSubtitle(entry) && (
                    <div className="text-xs text-label-secondary">
                      {formatEntryProjectSubtitle(entry)}
                    </div>
                  )}
                </>
              ) : (
                <span className="text-label-tertiary">—</span>
              );
            case "hours":
              return formatHours(entry.hours_decimal);
            case "time_range": {
              const range = formatEntryTimeRange(entry);
              return range || <span className="text-label-tertiary">—</span>;
            }
            case "entry_type":
              return (
                <span className="inline-flex rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-semibold text-label-secondary">
                  {formatEntryTypeLabel(entry.entry_type)}
                </span>
              );
            case "homeoffice":
              return entry.entry_type !== "break" && entry.is_homeoffice ? (
                <span className="inline-flex rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent">
                  Ja
                </span>
              ) : (
                <span className="text-label-tertiary">—</span>
              );
            case "comment":
              return entry.comment ? (
                <span className="line-clamp-2" title={entry.comment}>
                  {entry.comment}
                </span>
              ) : (
                <span className="text-label-tertiary">—</span>
              );
            default:
              return null;
          }
        },
      })),
    [],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 rounded-ios border border-separator bg-card p-4 shadow-ios md:grid-cols-4">
        <EmployeeLockedAutocomplete
          label="Mitarbeiter filtern"
          placeholder="Alle Mitarbeiter"
          value={employeeFilter}
          onChange={setEmployeeFilter}
        />
        <TextInput
          label="Von"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <TextInput
          label="Bis"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
        <div className="flex items-end">
          <Button variant="secondary" onClick={() => void load()}>
            Aktualisieren
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-label-secondary">
        <span>{entries.length} Einträge</span>
        <span className="font-medium text-label">Summe: {formatHours(totalHours)}</span>
      </div>

      {error && <Alert variant="error" title="Fehler">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <ZeiterfassungDataTable
          rows={entries}
          columns={columns}
          rowKey={(row) => row.id}
          widths={widths}
          onColumnWidthChange={onColumnWidthChange}
          emptyText="Keine Zeiteinträge für die gewählten Filter."
          actionColumn={{
            label: "Löschen",
            headerIcon: <span aria-hidden>🗑</span>,
            render: (entry) => (
              <button
                type="button"
                className="eh-data-action-btn"
                title="Eintrag löschen"
                disabled={deletingId === entry.id}
                onClick={() => void handleDelete(entry.id)}
              >
                <span className="text-[1.1rem] leading-none" aria-hidden>
                  ×
                </span>
              </button>
            ),
          }}
          renderMobileCard={(entry) => (
            <div className="flex flex-col gap-1.5 rounded-ios border border-separator bg-card px-4 py-3 shadow-ios">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm font-semibold text-label">
                  {formatEntryDateLabel(entry.work_date)}
                </span>
                <span className="font-medium tabular-nums text-accent">
                  {formatHours(entry.hours_decimal)}
                </span>
              </div>
              <div className="text-sm font-medium text-label">
                {entry.user_display || entry.user_login}
              </div>
              {entry.auftragsnummer && (
                <div>
                  <div className="flex flex-wrap items-center gap-1.5 text-sm text-label">
                    <span>{entry.auftragsnummer}</span>
                    <EmployeeTimeTimesheetBadge entry={entry} />
                  </div>
                  {formatEntryProjectSubtitle(entry) && (
                    <div className="text-xs text-label-secondary">
                      {formatEntryProjectSubtitle(entry)}
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 text-xs text-label-secondary">
                {formatEntryTimeRange(entry) && <span>{formatEntryTimeRange(entry)}</span>}
                <span className="rounded-full bg-fill-tertiary px-2 py-0.5 font-semibold">
                  {formatEntryTypeLabel(entry.entry_type)}
                </span>
                {entry.entry_type !== "break" && entry.is_homeoffice ? (
                  <span className="rounded-full bg-accent/10 px-2 py-0.5 font-semibold text-accent">
                    Homeoffice
                  </span>
                ) : null}
              </div>
              {entry.comment && (
                <p className="line-clamp-2 text-xs text-label-secondary">{entry.comment}</p>
              )}
              <div className="mt-1 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={deletingId === entry.id}
                  onClick={() => void handleDelete(entry.id)}
                >
                  Löschen
                </Button>
              </div>
            </div>
          )}
        />
      )}
    </div>
  );
}
